import React from 'react';

// State colors - Updated for readability on dark backgrounds
export const STATE_COLORS = {
  ground: '#4CAF82',     // bright forest green
  observe: '#4A90C4',    // clear medium blue
  connect: '#B06BB0',    // bright plum/mauve
  execute: '#D4723A',    // warm burnt orange
  integrate: '#7BAF4A'   // bright olive/leaf green
};

// State background tints (subtle)
export const STATE_BG = {
  ground: 'rgba(76, 175, 130, 0.09)',
  observe: 'rgba(74, 144, 196, 0.09)',
  connect: 'rgba(176, 107, 176, 0.09)',
  execute: 'rgba(212, 114, 58, 0.09)',
  integrate: 'rgba(123, 175, 74, 0.09)'
};

export const STATE_NAMES = {
  ground: 'Ground',
  observe: 'Observe',
  connect: 'Connect',
  execute: 'Execute',
  integrate: 'Integrate'
};

// Petal/Teardrop SVG Component - 12x16px default, scalable
export const PetalIndicator = ({ 
  state, 
  size = 'sm', 
  filled = true,
  active = false,
  completed = false,
  className = ''
}) => {
  const color = STATE_COLORS[state] || STATE_COLORS.ground;
  
  // Size presets
  const sizes = {
    xs: { width: 12, height: 16 },
    sm: { width: 16, height: 21 },
    md: { width: 24, height: 32 },
    lg: { width: 40, height: 52 }
  };
  
  const { width, height } = sizes[size] || sizes.sm;
  
  // If completed, show amber checkmark
  if (completed) {
    return (
      <svg 
        width={width} 
        height={height} 
        viewBox="0 0 24 32" 
        className={className}
      >
        <circle cx="12" cy="16" r="10" fill="#C8860A" />
        <path 
          d="M8 16 L11 19 L17 13" 
          stroke="white" 
          strokeWidth="2" 
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  
  return (
    <svg 
      width={width} 
      height={height} 
      viewBox="0 0 24 32" 
      className={`transition-all duration-300 ${active ? 'scale-110' : ''} ${className}`}
    >
      {/* Teardrop/Petal shape pointing upward */}
      <path 
        d="M12 2 C6 10, 4 18, 12 30 C20 18, 18 10, 12 2 Z"
        fill={filled ? color : 'transparent'}
        stroke={color}
        strokeWidth={filled ? 0 : 1.5}
        opacity={active ? 1 : (filled ? 0.8 : 0.5)}
      />
      {active && (
        <path 
          d="M12 2 C6 10, 4 18, 12 30 C20 18, 18 10, 12 2 Z"
          fill="none"
          stroke="#C8860A"
          strokeWidth="2"
          className="animate-pulse"
        />
      )}
    </svg>
  );
};

// State Sequence Display - Shows sequence of states with arrows
export const StateSequence = ({ 
  states, 
  currentIndex = -1,
  completedIndexes = [],
  size = 'sm',
  showLabels = false,
  className = '' 
}) => {
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {states.map((state, index) => (
        <React.Fragment key={`${state}-${index}`}>
          <div className="flex flex-col items-center">
            <PetalIndicator 
              state={state}
              size={size}
              filled={true}
              active={index === currentIndex}
              completed={completedIndexes.includes(index)}
            />
            {showLabels && (
              <span 
                className="text-[10px] mt-1 uppercase tracking-wider"
                style={{ color: index === currentIndex ? STATE_COLORS[state] : '#64748b' }}
              >
                {STATE_NAMES[state]}
              </span>
            )}
          </div>
          {index < states.length - 1 && (
            <svg width="16" height="8" viewBox="0 0 16 8" className="mx-0.5">
              <path 
                d="M0 4 L12 4 M10 1 L14 4 L10 7" 
                stroke={completedIndexes.includes(index) ? '#C8860A' : '#64748b'}
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Feature Card Icons based on PRD
export const FeatureIcons = {
  // Individual Ownership - Single upward petal (Ground)
  IndividualOwnership: ({ className = '' }) => (
    <svg width="40" height="52" viewBox="0 0 40 52" className={className}>
      <path 
        d="M20 4 C10 16, 6 28, 20 48 C34 28, 30 16, 20 4 Z"
        fill={STATE_COLORS.ground}
      />
    </svg>
  ),
  
  // Growth Trajectory - Three overlapping petals (Integrate)
  GrowthTrajectory: ({ className = '' }) => (
    <svg width="40" height="52" viewBox="0 0 40 52" className={className}>
      <path 
        d="M20 16 C14 24, 12 32, 20 44 C28 32, 26 24, 20 16 Z"
        fill={STATE_COLORS.integrate}
        opacity="0.4"
      />
      <path 
        d="M20 10 C12 20, 10 30, 20 46 C30 30, 28 20, 20 10 Z"
        fill={STATE_COLORS.integrate}
        opacity="0.6"
      />
      <path 
        d="M20 4 C10 16, 8 28, 20 48 C32 28, 30 16, 20 4 Z"
        fill={STATE_COLORS.integrate}
      />
    </svg>
  ),
  
  // Multi-Observer - Four petals toward center (Connect)
  MultiObserver: ({ className = '' }) => (
    <svg width="40" height="52" viewBox="0 0 40 52" className={className}>
      <g transform="translate(20, 26)">
        {/* Top petal */}
        <path d="M0 -20 C-4 -14, -4 -8, 0 0 C4 -8, 4 -14, 0 -20 Z" fill={STATE_COLORS.connect} />
        {/* Right petal */}
        <path d="M20 0 C14 -4, 8 -4, 0 0 C8 4, 14 4, 20 0 Z" fill={STATE_COLORS.connect} />
        {/* Bottom petal */}
        <path d="M0 20 C-4 14, -4 8, 0 0 C4 8, 4 14, 0 20 Z" fill={STATE_COLORS.connect} />
        {/* Left petal */}
        <path d="M-20 0 C-14 -4, -8 -4, 0 0 C-8 4, -14 4, -20 0 Z" fill={STATE_COLORS.connect} />
      </g>
    </svg>
  ),
  
  // AI-Powered Synthesis - Open lotus with amber core (Observe)
  AIPoweredSynthesis: ({ className = '' }) => (
    <svg width="40" height="52" viewBox="0 0 40 52" className={className}>
      <g transform="translate(20, 26)">
        {/* Five radiating petals */}
        {[0, 72, 144, 216, 288].map((angle, i) => (
          <path 
            key={i}
            d="M0 -18 C-3 -12, -3 -6, 0 0 C3 -6, 3 -12, 0 -18 Z" 
            fill={STATE_COLORS.observe}
            opacity="0.6"
            transform={`rotate(${angle})`}
          />
        ))}
        {/* Amber center */}
        <circle cx="0" cy="0" r="4" fill="#C8860A" />
      </g>
    </svg>
  )
};

// State Legend Component
export const StateLegend = ({ states = ['ground', 'observe', 'connect', 'execute', 'integrate'], className = '' }) => {
  return (
    <div className={`flex flex-wrap gap-4 ${className}`}>
      {states.map(state => (
        <div key={state} className="flex items-center gap-2">
          <PetalIndicator state={state} size="xs" />
          <span className="text-sm text-white/80">{STATE_NAMES[state]}</span>
        </div>
      ))}
    </div>
  );
};

export default PetalIndicator;
